import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, Paper, IconButton ,makeStyles, Grid, Typography,
  Box
} from '@material-ui/core';
import { Print, Search } from '@material-ui/icons';
import instance from 'api/globalaxios';
import { format } from 'date-fns';
import DateRangePickerTDSAT from '../DateRangePickerTDSAT';
import CircularCenteredLoader from 'features/common/CircularCenteredLoader';
import Swal from 'sweetalert2';
import fileDownload from 'js-file-download';
import { viewScrutinyReport } from '../API/TdsatApi';
import { useSnackbar } from "contexts/SnackbarContext";
import { Pagination } from '@material-ui/lab';
import validateDateAndFormat from 'utils/validateDateAndFormat';
import Tabs from 'features/Tabs/Tabs';

const useStyles = makeStyles((theme) => ({
  root: {
		padding: '1.5rem',
    width: '100%',
    boxSizing: 'border-box',
	},
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: '15px',
    [theme.breakpoints.down("xs")]: {
			flexWrap: 'wrap',
		},
  },
  searchField: {
    flexGrow: 1,
    borderRadius: '8px',
  },
  table: {
    minWidth: 650,
    width: '100%',
    maxWidth: '100%'
  },
  noDefect: {
    color: 'green',
  },
  pagination: {
    marginTop: theme.spacing(2),
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  datePickers: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  noBorders: {
    border: 'none',
    fontSize: '14px',
    color: '#242529',
  },
  noBordersTitle: {
    border: 'none',
    fontSize: '15px',
    fontFamily: 'Satoshi-Bold'
  },
  buttonPrint: {
    borderRadius: '8px',
    border: '1px solid var(--BG-Color-1, #AAAFB9)',
    background: '#fff',
    padding: '9px 14px',
    minWidth: 'fit-content',
  },
  tableRow: {
    '& th, & td': { 
      padding: '1.5rem !important',  
    },
    border: '1px solid #E5E5E5',
    background: '#fff',
  },
  titleTop: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '30px'
  },
  noBordersName: {
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    border: 'none',
    fontSize: '15px',
  },
  tabsContainer: {
    border: '1px solid #858D98',
    width: 'fit-content',
    padding: '0.125rem',
    borderRadius: '0.5rem',
  },
  selected: {
    padding: "0.5rem 1.25rem",
    cursor: 'pointer',
    fontFamily: 'Satoshi-Medium',
    minWidth: 'fit-content',
    textAlign: 'center',
    borderRadius: '0.5rem',
    color: 'white',
    backgroundColor: theme.palette.primary.main,
  },
  unselected: {
    padding: "0.5rem 1.25rem",
    cursor: "pointer",
    fontFamily: 'Satoshi-Medium',
    minWidth: 'fit-content',
    textAlign: 'center',
    borderRadius: '0.5rem',
  },
}));

const AdminScrutinyReport = () => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [countPagination, setCountPagination] = useState(0);
  const PAGE_SIZE = 10;
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false); 
  const [activeTab, setActiveTab] = useState('registered');
  const { showSnackbar } = useSnackbar();

  const IS_DEFECTIVE_CASE = activeTab === 'under_objection';

  useEffect(() => {
    fetchScrutinyReportData();
  },[])

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchScrutinyReportData(searchTerm, startDate, endDate, newPage, activeTab); // Update data on page change
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePrintReport = async () => {
    if(!startDate || !endDate) {
      showSnackbar('Start date and End date are required.','error');
      return;
    }
    try {
      let url = `tdsat/scrutiny_report/generate?type=main_cases`;

      if (startDate && endDate) {
          url += `&start_date=${format(startDate, 'dd-MM-yyyy')}&end_date=${format(endDate, 'dd-MM-yyyy')}`;
      }

      const response = await instance.get(url, { responseType: "blob", });

      if (response.data) {
        // Create a URL for the blob
        const blobUrl = URL.createObjectURL(response.data);
        const newWindow = window.open(blobUrl, '_blank');
        // Optional: Add a check for pop-up blockers
        if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
          Swal.fire({
            title: "Pop-up Blocked!",
            text: "Please enable pop-ups for this site to view the PDF report.",
            icon: "warning"
          });
        }
      } else {
        Swal.fire({
          title: "Error",
          text: "No PDF data received from the server.",
          icon: "error"
        });
      }

    } catch (error) {
        console.error("There was an error fetching the case data!", error);
    }
  };

  const handleSubmit = () => {
    fetchScrutinyReportData(searchTerm, startDate, endDate); // Fetch data on Submit button click
  };

  const fetchScrutinyReportData = async (searchText = '', startDate = null, endDate = null, page = 1, caseStatus = activeTab) => {
    setLoading(true); 
    try {
      const params = {
        type: 'main_cases',
        dashboard_type: 'registered_cases',
        search_text: searchText || '',
        start_date: startDate ? format(startDate, 'dd-MM-yyyy') : '',
        end_date: endDate ? format(endDate, 'dd-MM-yyyy') : '',
        page: page > 1 ? page : 1,
        case_status: caseStatus,
      };
      const response = await instance.get('/tdsat/dashboard-search/', { params });
      setFilteredData(response?.data?.results || []);
      let count = response?.data?.count || 0;
      let total = Math.ceil(count / PAGE_SIZE);
      setCountPagination(total);
    } catch (error) {
      console.error('There was an error fetching the case data!', error);
    }
    setLoading(false);
  };

  const handleViewScrutinyReport = async (caseName, diary_number) => {
		try {
			const response = await viewScrutinyReport(caseName, diary_number);
      console.log(response,'response')
			const link = response.data?.pre_signed_url;
			if (link) {
				const newTab = window.open();
				newTab.location.href = link;
			} else {
        showSnackbar(response?.data?.error, 'error');
				console.error("Link not found in response");
			}
		} catch (error) {
      if(error?.response?.status === 400){
        showSnackbar(error?.response?.data?.error, 'error');
    }
			console.error("Failed to fetch the notice link", error);
		}
	};

  const tabs = [
		{
			id: "registered",
			label: "Registered",
		},
		{
			id: "under_objection",
			label: "Defective",
		},
	];

  const handleTabChange = (newValue) => {
		setActiveTab(newValue);
    fetchScrutinyReportData(searchTerm, startDate, endDate, page, newValue);
	};

  return (
    <div className={classes.root}>
      <Grid item>
        <Typography className={classes.titleTop}>
          CHECK SCRUTINY REPORT
        </Typography>
      </Grid>
      <div className={classes.searchContainer}>
        <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <TextField
            className={classes.searchField}
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            placeholder="Parties name/Diary no./Case no."
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleSubmit}
                  style={{ padding: "8px" }}
                  disabled={!searchTerm}
                >
                  <Search />
                </IconButton>
              ),
            }}
          />
        </div>

        {/* Date Range Picker */}
        <div className={classes.datePickers}>
          <DateRangePickerTDSAT
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          size="small"
          className={classes.buttonPrint}
        >
          Submit
        </Button>

        {/* Print Button */}
        <Button
          onClick={handlePrintReport}
          size="small"
          className={classes.buttonPrint}
          startIcon={<Print />}
        >
          Print Report
        </Button>
      </div>
      <Box display={"flex"} justifyContent={"flex-end"}>
        <Tabs
          tabs={tabs}
          handleClickTab={handleTabChange}
          choice={activeTab}
          tabsClasses={{
            selected: classes.selected,
            unselected: classes.unselected,
          }}
          tabsContainerClass={classes.tabsContainer}
        />
      </Box>

      {loading ? (
        <div className={classes.loader}>
          <CircularCenteredLoader />
        </div>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.tableRow}>
                  <TableCell className={classes.noBordersTitle}>
                    Sr. No.
                  </TableCell>
                  <TableCell className={classes.noBordersTitle}>
                    Diary No.
                  </TableCell>
                  <TableCell className={classes.noBordersTitle}>
                    Case No.
                  </TableCell>
                  <TableCell className={classes.noBordersTitle}>
                    Party Name
                  </TableCell>
                  <TableCell className={classes.noBordersTitle}>
                    Petitioner Advocate
                  </TableCell>
                  <TableCell className={classes.noBordersTitle}>
                    Defect
                  </TableCell>
                  <TableCell className={classes.noBordersTitle}>
                    Date of Filing
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData?.map((row, index) => (
                  <TableRow key={index} className={classes.tableRow}>
                    <TableCell>
                      {(page - 1) * rowsPerPage + (index + 1)}
                    </TableCell>
                    <TableCell 
                      style={IS_DEFECTIVE_CASE ? { color: "#4a3bff", cursor: "pointer" } : null}
                      className={classes.noBorders}
                      onClick={IS_DEFECTIVE_CASE ? () => handleViewScrutinyReport('', row?.diary_number) : undefined}
                    >
                      {row.diary_number}
                    </TableCell>
                    <TableCell
                      style={!IS_DEFECTIVE_CASE ? { color: "#4a3bff", cursor: "pointer" } : null}
                      className={classes.noBorders}
                      onClick={!IS_DEFECTIVE_CASE ? () => handleViewScrutinyReport(row?.case_number) : undefined}
                    >
                      {row.case_number}
                    </TableCell>
                    <TableCell className={classes.noBordersName}>
                      {row.name}
                    </TableCell>
                    <TableCell className={classes.noBorders}>
                      {row?.petitioner_advocate?.map(
                        (advocate, advocateIndex) => (
                          <span key={advocateIndex}>
                            {advocate?.name}
                            {advocateIndex <
                              row.petitioner_advocate.length - 1 && <br />}
                          </span>
                        )
                      )}
                    </TableCell>
                    <TableCell className={classes.noBorders}>
                      {row?.register_with_defect
                        ? "Under Objection"
                        : row?.defects?.length === 0
                        ? "No"
                        : "Yes"}
                    </TableCell>
                    <TableCell className={classes.noBorders}>
                      {validateDateAndFormat(
                        row.case_creation_date_time,
                        "dd/MM/y"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredData.length !== 0 && (
            <Box
              style={{
                padding: "16px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Pagination
                color="primary"
                count={countPagination}
                page={page}
                onChange={handleChangePage}
              />
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default AdminScrutinyReport;
